import Modal from "react-bootstrap/Modal";
import { useEffect, useMemo, useRef, useState } from "react";
import InputController from "../../../atoms/InputController/General/TextField-input/InputController";
import {
  handleCreateChat,
  handleEditChat,
} from "../../../../pages/leads-management/service/LeadService";
import UseAuthConfig from "../../../../hooks/useAuthConfig";
import { AppDispatch } from "../../../../slices/store";
import { useDispatch } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useLeadData } from "../../../../hooks/useLeads";
import * as signalR from "@microsoft/signalr";
import useLoggedUser from "../../../../hooks/useLoggedUser";
import IconHome from "../../../../styles/icons/IconHome";
interface Props {
  onHide: () => void;
  show: boolean;
  header: string;
  bodyHeadline: string;
  bodyContent: string;
  name: string;
}

const ChatModal = (props: Props) => {
  const { selectedLead } = useLeadData();
  const { protectedResources } = UseAuthConfig();
  const dispatch = useDispatch<AppDispatch>();
  const [chatValue, setChatValue] = useState<string>("");
  const [editingCommentId] = useState<number | null>(null);
  const [comments, setComments] = useState(
    (selectedLead?.assignedUsers[0]?.comments || []).slice().reverse()
  );
  const leadUserId = selectedLead?.assignedUsers[0]?.leadUserId;
  const selectedLeadId = selectedLead?.id;
  const prevLeadIdRef = useRef<number | null>(null);
  const chatListRef = useRef<HTMLUListElement>(null);
  const loggedUser = useLoggedUser();
  const connection = useMemo(() => {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_INFINITYS_API}/notificationhub`, {
        withCredentials: false,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();
  }, []);
  const formatCommentDateTime = (createdAt: string) => {
    const dateObj = new Date(createdAt);

    return {
      date: dateObj.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: dateObj.toLocaleTimeString("en-AU", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  useEffect(() => {
    if (!loggedUser) return;
    const onPostComment = (note: any) => {
      setComments((prevComments) => {
        const exists = prevComments.some((c) => c.id === note.id);
        if (exists) return prevComments;
        const isCurrentUserComment = note.userId === loggedUser?.userId;

        return [...prevComments, { ...note, isCurrentUserComment }];
      });
    };

    connection.on("postcomment", onPostComment);

    return () => {
      connection.off("postcomment", onPostComment);
    };
  }, [connection, leadUserId, loggedUser]);

  useEffect(() => {
    if (chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
    }
  }, [comments]);

  useEffect(() => {
    let cancelled = false;

    async function startConnection() {
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        try {
          await connection.start();
        } catch (err) {
          console.error("❌ SignalR connection error:", err);
        }
      }
    }

    async function stopConnection() {
      if (connection.state !== signalR.HubConnectionState.Disconnected) {
        try {
          await connection.stop();
        } catch (err) {
          console.error("❌ Error stopping SignalR:", err);
        }
      }
    }

    async function manageSubscription() {
      if (!selectedLeadId) return;
      if (prevLeadIdRef.current === selectedLeadId) return;

      try {
        if (prevLeadIdRef.current) {
          await connection.invoke("UnsubscribeFromLead", prevLeadIdRef.current);
        }
        await connection.invoke("SubscribeToLead", selectedLeadId);
        prevLeadIdRef.current = selectedLeadId;
      } catch (err) {
        console.error("❌ Error subscribing to lead:", err);
      }
    }

    if (props.show) {
      (async () => {
        await startConnection();
        if (!cancelled) {
          await manageSubscription();
        }
      })();
    } else {
      stopConnection();
    }

    return () => {
      cancelled = true;
    };
  }, [props.show, selectedLeadId, connection]);

  function OnChatChange(e: any) {
    setChatValue(e.target.value);
  }

  function OnSubmit() {
    if (!selectedLeadId || !leadUserId) return;

    if (editingCommentId !== null) {
      const note = {
        id: editingCommentId,
        content: chatValue,
      };
      handleEditChat(protectedResources, note, dispatch, selectedLeadId);
    } else {
      const note = {
        leadUserId,
        content: chatValue,
        parentId: 0,
        leadId: selectedLeadId,
        id: leadUserId,
      };
      handleCreateChat(protectedResources, note, dispatch, selectedLeadId);
    }

    setChatValue("");
  }

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="base-modal"
    >
      <Modal.Header closeButton className="header">
        <Modal.Title id="contained-modal-title-vcenter" className="title">
          <IconHome breadcrumb="chatIcon" />
          <div>
            {props.header}
            <p>Send a message via Email</p>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="body">
        <div className="chat-wrapper">
          <div className="left-section">
            <div className="section-info">
              <h4>
                {selectedLead?.firstName} {""}
                {selectedLead?.lastName}
              </h4>
              <p>{selectedLead?.mobile}</p>
            </div>
            <div className="section-info">
              <h5>About this lead</h5>
              <div>
                <p className="d-flex align-items-start gap-3">
                  <IconHome breadcrumb="locationIcon" />
                  <div>
                    {selectedLead?.addressOne.addressLineOne},
                    {selectedLead?.addressOne.addressLineTwo},
                    {selectedLead?.addressOne.city},
                    {selectedLead?.addressOne.state},
                    {selectedLead?.addressOne.country}
                  </div>
                </p>
                <p className="d-flex align-items-center gap-3 email">
                  <IconHome breadcrumb="emailIcon" />
                  {selectedLead?.email}
                </p>
              </div>
            </div>
            <div className="section-info">
              <h5>Lead Owner</h5>
              <div>
                <p className="d-flex align-items-start gap-3">
                  <IconHome breadcrumb="profilePicture" />
                  {selectedLead?.assignedUsers[0]?.firstName}{" "}
                  {selectedLead?.assignedUsers[0]?.lastName} (You)
                </p>
              </div>
            </div>
          </div>
          <div className="chat-container w-100">
            <ul className="chat-list" ref={chatListRef}>
              {comments.map((comment) => (
                <li
                  key={comment.id}
                  className={`chat-message ${
                    comment.isCurrentUserComment
                      ? "justify-right"
                      : "justify-left"
                  }`}
                >
                  <div className="message-content">
                    <Row>
                      <Col md={12}>
                        {!comment.isCurrentUserComment && (
                          <div className="custom-header">
                            {comment.createdByFirstName}{" "}
                            {comment.createdByLastName} |
                            on {formatCommentDateTime(comment.createdAt).date}
                          </div>
                        )}
                        <div className="message-content-wrapper">
                          <div className="content clearfix">
                            <p>
                              {/* <span className="avatar-circle">
                              {comment.createdByFirstName.charAt(0) +
                                comment.createdByLastName.charAt(0)}
                            </span> */}
                              {comment.content}
                            </p>
                            <small>
                              {formatCommentDateTime(comment.createdAt).time}
                            </small>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </li>
              ))}
            </ul>

            <div className="chat-input">
              <InputController
                value={chatValue}
                name="chat"
                placeholder=""
                type="text"
                onChange={OnChatChange}
                label=""
                fullWidth={true}
              />
              <button className="submit-btn" onClick={OnSubmit}>
                <img
                  width="24"
                  height="24"
                  src="https://img.icons8.com/material-rounded/48/sent.png"
                  alt="sent"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ChatModal;
